import { useEffect, useState } from 'react'
import { Check, Trash2, X } from 'lucide-react'
import { AssigneePicker } from './AssigneePicker'
import { DueDateField } from './DueDateField'
import type { CardPatch } from '../lib/api'
import type { Assignee, Card } from '../lib/types'

type Props = {
  card: Card
  onClose: () => void
  onSave: (id: string, patch: CardPatch) => void
  onToggleDone: (card: Card) => void
  onDelete: (card: Card) => void
}

type Draft = {
  title: string
  notes: string
  assignee: Assignee
  dueDate: string | null
  waitingOn: string
}

const draftOf = (card: Card): Draft => ({
  title: card.title,
  notes: card.notes ?? '',
  assignee: card.assignee,
  dueDate: card.dueDate,
  waitingOn: card.waitingOn ?? '',
})

export function CardDetail({ card, onClose, onSave, onToggleDone, onDelete }: Props) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(card))
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    setDraft(draftOf(card))
    setConfirmingDelete(false)
  }, [card])

  useEffect(() => {
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [onClose])

  const original = draftOf(card)
  const isWaiting = card.status === 'waiting'
  const dirty =
    draft.title.trim() !== original.title ||
    draft.notes !== original.notes ||
    draft.assignee !== original.assignee ||
    draft.dueDate !== original.dueDate ||
    (isWaiting && draft.waitingOn !== original.waitingOn)

  const save = () => {
    const title = draft.title.trim()
    if (!title) return
    const patch: CardPatch = {}
    if (title !== original.title) patch.title = title
    if (draft.notes !== original.notes) patch.notes = draft.notes.trim() || null
    if (draft.assignee !== original.assignee) patch.assignee = draft.assignee
    if (draft.dueDate !== original.dueDate) patch.dueDate = draft.dueDate
    // Only writable from Waiting, so a card in another column can't silently
    // clear a value its form never showed.
    if (isWaiting && draft.waitingOn !== original.waitingOn) {
      patch.waitingOn = draft.waitingOn.trim() || null
    }
    if (Object.keys(patch).length > 0) onSave(card.id, patch)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink/40 p-4 sm:p-8"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Card details"
        className="w-full max-w-lg rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-cream-100 p-5 pb-4">
          <textarea
            autoFocus
            rows={2}
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            aria-label="Card title"
            className="min-w-0 flex-1 resize-none text-base leading-snug font-semibold text-ink
                       focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-8 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-cream-100"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <div>
            <label className="mb-2 block text-xs font-semibold tracking-wide text-muted uppercase">
              Assigned to
            </label>
            <AssigneePicker
              size="md"
              value={draft.assignee}
              onChange={(assignee) => setDraft((d) => ({ ...d, assignee }))}
            />
          </div>

          <div>
            <span className="mb-2 block text-xs font-semibold tracking-wide text-muted uppercase">
              Due date
            </span>
            <DueDateField
              value={draft.dueDate}
              onChange={(dueDate) => setDraft((d) => ({ ...d, dueDate }))}
            />
          </div>

          {isWaiting && (
            <div>
              <label
                htmlFor="card-waiting-on"
                className="mb-2 block text-xs font-semibold tracking-wide text-muted uppercase"
              >
                Waiting on
              </label>
              <input
                id="card-waiting-on"
                type="text"
                maxLength={200}
                value={draft.waitingOn}
                onChange={(e) => setDraft((d) => ({ ...d, waitingOn: e.target.value }))}
                placeholder="Who or what is holding this up?"
                className="field"
              />
            </div>
          )}

          <div>
            <label
              htmlFor="card-notes"
              className="mb-2 block text-xs font-semibold tracking-wide text-muted uppercase"
            >
              Notes
            </label>
            <textarea
              id="card-notes"
              rows={4}
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder="Anything else worth knowing…"
              className="field resize-y"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-cream-100 p-5 pt-4">
          {confirmingDelete ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted">Delete this card?</span>
              <button
                type="button"
                onClick={() => {
                  onDelete(card)
                  onClose()
                }}
                className="rounded-lg bg-flame px-3 py-1.5 text-xs font-semibold text-white"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="rounded-lg px-2 py-1.5 text-xs font-medium text-muted hover:bg-cream-100"
              >
                Keep
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium
                         text-muted transition hover:bg-cream-100 hover:text-flame"
            >
              <Trash2 className="size-4" />
              Delete
            </button>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onToggleDone(card)
                onClose()
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-cream-200 px-3 py-2
                         text-sm font-medium text-ink transition hover:border-grass hover:text-grass"
            >
              <Check className="size-4" />
              Mark done
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || !draft.title.trim()}
              className="rounded-lg bg-teal px-4 py-2 text-sm font-semibold text-white transition
                         hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
